import React, { useEffect, useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { money, num, ago } from "@/lib/sk";
import { H } from "@/components/canvas/kalkyle/PostBlocks";

const K = (v) => `${money(v, "NOK", 1, { bare: true })} kr`;

/**
 * Historikk for one post, where the work is: what it was in each frozen version, and every logged change that touched it –
 * «Egil · i går · 200.1: mengde 12 → 16». Collapsed to one line until asked; loaded first when opened.
 */
export function PostHistory({ caseId, item, testId = "detail" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const key = `${item.id}|${item.updated_at || ""}|${(item.history || []).length}`;
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    api.get(`/projects/${caseId}/versjoner/post/${item.id}`).then((r) => alive && setData(r.data)).catch((e) => alive && setErr(errMsg(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseId, key]);
  const n = (item.history || []).length;
  const last = n ? item.history[n - 1] : null;
  const line = last ? `Sist endret ${ago(last.at)}${last.by ? ` av ${last.by}` : ""}` : "Ingen endringer ennå";
  return (
    <section data-testid={`${testId}-history-${item.ref}`} data-open={open ? "1" : "0"}>
      <H right={<button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-[13px] font-medium text-[hsl(var(--navy))] hover:underline" data-testid={`${testId}-history-toggle-${item.ref}`}>{open ? "Skjul" : "Vis"} <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} /></button>}>Historikk</H>
      {!open ? <p className="k-meta mt-1.5">{line}</p> : err ? <p className="mt-1.5 text-[13.5px] text-[hsl(var(--v-blocked))]">{err}</p> : !data ? <div className="mt-2 flex h-10 items-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
        <div className="mt-2 space-y-4">
          {data.versions.length ? (
            <ul className="space-y-1" data-testid={`${testId}-history-versions-${item.ref}`}>
              {data.versions.map((v) => (
                <li key={v.revision} className="flex items-baseline gap-3 text-[14px]" data-testid={`${testId}-history-v${v.revision}-${item.ref}`}>
                  <span className="w-[84px] shrink-0 text-muted-foreground">Versjon {v.revision}</span>
                  {v.present ? <span className="min-w-0 flex-1 text-foreground/85">{v.price?.text || "–"}{v.hours ? ` · ${num(v.hours, 0)} t` : ""}{v.cost_total != null ? ` · kost ${K(v.cost_total)}` : ""}{v.state_word ? ` · ${v.state_word}` : ""}{v.thin ? <span className="k-meta"> · kun kundepris fryst</span> : null}</span> : <span className="min-w-0 flex-1 text-muted-foreground">ikke med</span>}
                  <span className="k-meta shrink-0">{ago(v.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : <p className="k-meta">Ingen tilbudsversjon er laget ennå.</p>}
          {data.changes.length ? (
            <ul className="space-y-1 border-t border-black/[0.06] pt-3" data-testid={`${testId}-history-changes-${item.ref}`}>
              {data.changes.slice(0, 12).map((c) => (
                <li key={c.id} className={cn("flex items-baseline gap-3 text-[14px]", c.undone && "text-muted-foreground line-through decoration-black/20")}>
                  <span className="w-[84px] shrink-0 text-muted-foreground">{ago(c.at)}</span>
                  <span className="min-w-0 flex-1 text-foreground/85">{c.label}</span>
                  <span className="k-meta shrink-0">{c.by || "system"}</span>
                </li>
              ))}
              {data.changes.length > 12 ? <li className="k-meta">+{data.changes.length - 12} eldre</li> : null}
            </ul>
          ) : null}
        </div>
      )}
    </section>
  );
}
